import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PUBLIC_SEO_LANDING_PAGES } from "../seo/publicLandingPages";
import "../styles/pages/PublicSeoLandingPage.css";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.reelitpro.app";

export default function PublicSeoLandingPage({ pageKey }) {
  const { t } = useTranslation();
  const page = PUBLIC_SEO_LANDING_PAGES[pageKey];

  if (!page) {
    return null;
  }

  return (
    <main className="public-seo-page">
      <header className="public-seo-header">
        <Link className="public-seo-brand" to="/">
          <img src="/logo192.png" alt="" className="public-seo-brand-mark" />
          <span>ReelitPro</span>
        </Link>
        <nav className="public-seo-nav" aria-label={t("public_seo_nav_aria")}>
          <Link to="/fishipedia">{t("fishipedia_title")}</Link>
          <Link to="/regulations">{t("public_seo_nav_regulations")}</Link>
          <Link to="/login">{t("public_seo_nav_login")}</Link>
        </nav>
      </header>

      <section className="public-seo-hero">
        <div className="public-seo-hero-copy">
          <p className="public-seo-eyebrow">{t(page.eyebrowKey)}</p>
          <h1>{t(page.headlineKey)}</h1>
          <p className="public-seo-intro">{t(page.introKey)}</p>
          <div className="public-seo-actions">
            <Link className="public-seo-primary" to="/register">
              {t(page.ctaKey)}
            </Link>
            <Link className="public-seo-secondary" to="/fishipedia">
              {t("public_seo_explore_fishipedia")}
            </Link>
            <a
              className="public-seo-secondary"
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("public_seo_download_google_play")}
            </a>
          </div>
        </div>

        <div className="public-seo-hero-panel" aria-hidden="true">
          <div className="public-seo-panel-card public-seo-panel-card-primary">
            <span>{t("public_seo_panel_plan_label")}</span>
            <strong>{t("public_seo_panel_plan_body")}</strong>
          </div>
          <div className="public-seo-panel-card">
            <span>{t("public_seo_panel_explore_label")}</span>
            <strong>{t("public_seo_panel_explore_body")}</strong>
          </div>
          <div className="public-seo-panel-card">
            <span>{t("public_seo_panel_learn_label")}</span>
            <strong>{t("public_seo_panel_learn_body")}</strong>
          </div>
        </div>
      </section>

      <section className="public-seo-section" aria-labelledby={`${pageKey}-features`}>
        <h2 id={`${pageKey}-features`}>{t("public_seo_features_title")}</h2>
        <div className="public-seo-card-grid">
          {page.features.map((feature) => (
            <article className="public-seo-card" key={feature.titleKey}>
              <h3>{t(feature.titleKey)}</h3>
              <p>{t(feature.bodyKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="public-seo-section public-seo-help">
        <div>
          <h2>{t(page.helpTitleKey)}</h2>
          <ul>
            {page.helpItemKeys.map((itemKey) => (
              <li key={itemKey}>{t(itemKey)}</li>
            ))}
          </ul>
        </div>
      </section>

      {page.sections?.length ? (
        <section className="public-seo-section" aria-labelledby={`${pageKey}-guide`}>
          <h2 id={`${pageKey}-guide`}>
            {t(page.sectionsTitleKey || "public_seo_guide_title")}
          </h2>
          <div className="public-seo-detail-grid">
            {page.sections.map((section) => (
              <article className="public-seo-detail" key={section.titleKey}>
                <h3>{t(section.titleKey)}</h3>
                <p>{t(section.bodyKey)}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="public-seo-section" aria-labelledby={`${pageKey}-links`}>
        <h2 id={`${pageKey}-links`}>{t("public_seo_links_title")}</h2>
        <div className="public-seo-link-grid">
          {page.links.map((link) => (
            <Link className="public-seo-link-card" to={link.to} key={link.to}>
              <span>{t(link.labelKey)}</span>
              <p>{t(link.descriptionKey)}</p>
            </Link>
          ))}
        </div>
      </section>

      {page.faqs?.length ? (
        <section className="public-seo-section" aria-labelledby={`${pageKey}-faq`}>
          <h2 id={`${pageKey}-faq`}>{t(page.faqTitleKey)}</h2>
          <div className="public-seo-faq-list">
            {page.faqs.map((faq) => (
              <article className="public-seo-faq" key={faq.questionKey}>
                <h3>{t(faq.questionKey)}</h3>
                <p>{t(faq.answerKey)}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
